import { useTranslation } from '@mukil/i18n';
import { GlassCard } from '@jumpstone/ui-react';
import {
  format_month_year,
  format_relative_time,
  format_time,
  is_same_utc_day,
} from '../../lib/format';
import type { ActivityEvent } from '../../data/types';

const ACTIVITY_KEY = {
  exceptions_acknowledged: 'dashboard.activity.exceptions_acknowledged',
  pay_run_created: 'dashboard.activity.pay_run_created',
  pay_run_approved: 'dashboard.activity.pay_run_approved',
  expense_submitted: 'dashboard.activity.expense_submitted',
} as const;

/** Reverse-chronological audit feed for the current company. */
export function ActivityFeedCard({
  events,
  now,
}: {
  events: ActivityEvent[];
  now: string;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');

  return (
    <GlassCard className="rounded-2xl p-5">
      <h2 className="text-[14.5px] font-bold mb-2.5">
        {t('dashboard.recent_activity')}
      </h2>
      {events.map((event) => (
        <div
          key={event.id}
          className="flex gap-2.5 py-2 text-[13px] border-b border-[color:var(--line)] last:border-b-0"
        >
          <span className="text-[color:var(--ink-muted)] min-w-[76px] num">
            {is_same_utc_day(event.at, now)
              ? format_time(event.at, locale)
              : format_relative_time(event.at, now, locale)}
          </span>
          <span>{describe(event, t, locale)}</span>
        </div>
      ))}
    </GlassCard>
  );
}

function describe(
  event: ActivityEvent,
  t: (key: string, params?: Record<string, string | number>) => string,
  locale: string,
): string {
  switch (event.kind) {
    case 'exceptions_acknowledged':
      return t(ACTIVITY_KEY[event.kind], {
        name: event.actor,
        count: event.count,
      });
    case 'pay_run_created':
      return t(ACTIVITY_KEY[event.kind], {
        period: format_month_year(event.period, locale),
      });
    case 'pay_run_approved':
      return t(ACTIVITY_KEY[event.kind], {
        name: event.actor,
        period: format_month_year(event.period, locale),
      });
    case 'expense_submitted':
      return t(ACTIVITY_KEY[event.kind], { name: event.actor });
  }
}
